import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from 'react-redux';
import DateRangePicker from "../../components/date-range-picker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  Alert,
  Divider,
  FormControlLabel,
  Switch
} from "@mui/material";
import { Download, Refresh } from "@mui/icons-material";
import dayjs from "dayjs";
import { useAnalyticsSyncDate } from "../../context/analytics-sync-date.context";

// Import custom components and hooks
import { AgentAnalyticsTable } from "./components/AgentAnalyticsTable";
import { UserFilterDropdown } from "./components/UserFilterDropdown";
import { useGetAgentAnalytics } from "../../../../../../features/analytics/hooks/useGetAgentAnalytics";
import { useExportAgentAnalytics } from "../../../../../../features/analytics/hooks/exports/useExportAgentAnalytics";

export const AgentAnalytics = () => {
  // State management

  const [selectedUsers, setSelectedUsers] = useState(['all']);
  const [exportLoading, setExportLoading] = useState(false);
  const [uniqueCalls, setUniqueCalls] = useState(false);
  const { fromDate: syncedFromDate, toDate: syncedToDate, enableDateSync } = useAnalyticsSyncDate();

  const [fromDate, setFromDate] = useState(() => enableDateSync ? dayjs(syncedFromDate) : dayjs().startOf('month'));
  const [toDate, setToDate] = useState(() => enableDateSync ? dayjs(syncedToDate) : dayjs().endOf('month'));

  // Redux state
  const { users } = useSelector((state) => state.user || {});

  // Filter users to get only telecallers using environment variable
  const telecallerUsers = useMemo(() => {
    if (!users || !Array.isArray(users)) return [];

    // Filter for active telecaller users using environment variable
    return users.filter(user =>
      user.status === 'active' &&
      user.roles &&
      user.roles.includes(process.env.REACT_APP_TELECALLER)
    );
  }, [users]);

  // Export hook
  const exportMutation = useExportAgentAnalytics({
    onSuccess: (data) => {
      console.log('Agent analytics exported successfully:', data);
      setExportLoading(false);
    },
    onError: (error) => {
      console.error('Export failed:', error);
      setExportLoading(false);
    }
  });

  // Event handlers
  const handleFromChange = (newDate) => setFromDate(newDate ? dayjs(newDate) : null);
  const handleToChange = (newDate) => setToDate(newDate ? dayjs(newDate) : null);

  const handleUserChange = (newUsers) => setSelectedUsers(newUsers);
  const handleUniqueCallsToggle = (e) => setUniqueCalls(e.target.checked);

  const queryParams = useMemo(() => {
    const base = {};
    // backend expects `from` and `to` query params (ISO YYYY-MM-DD)
    if (fromDate) base.from = dayjs(fromDate).format('YYYY-MM-DD');
    if (toDate) base.to = dayjs(toDate).format('YYYY-MM-DD');

    if (!selectedUsers.includes('all') && selectedUsers.length > 0) base.allocatedToUser = selectedUsers;
    if (uniqueCalls) base.uniqueCalls = true;
    return base;
  }, [fromDate, toDate, selectedUsers, uniqueCalls]);

  // Fetch agent analytics data (new hook signature)
  const { data: analyticsResponse, isFetching, error, refetch } = useGetAgentAnalytics(queryParams);

  // Derived data
  const agentData = analyticsResponse?.data?.agents || [];
  const summary = analyticsResponse?.data?.summary;

  // Handlers needing hook data
  const handleRefresh = () => refetch();
  const handleExport = () => {
    setExportLoading(true);
    exportMutation.mutate(queryParams);
  };
  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3, overflowX: "hidden" }}>
      <Typography variant="h5" component="h2" gutterBottom>
        Agent Performance Analytics
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        This view counts allocations per agent. These totals may not match report
        counts because a single allocation can be attempted multiple times, and
        each attempt generates a report.
      </Alert>

      {/* Controls Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" component="h3" gutterBottom>
          Filter Options
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <DateRangePicker
              fromDate={fromDate}
              toDate={toDate}
              onFromChange={handleFromChange}
              onToChange={handleToChange}
            />

            <UserFilterDropdown
              users={telecallerUsers}
              selectedUsers={selectedUsers}
              onUserChange={handleUserChange}
              label="Filter by Agents"
              placeholder="Select agents to analyze"
              loading={!users}
            />

            <Button
              variant="outlined"
              onClick={handleRefresh}
              startIcon={<Refresh />}
              disabled={isFetching}
            >
              Refresh
            </Button>

            <FormControlLabel
              control={<Switch checked={uniqueCalls} onChange={handleUniqueCallsToggle} />}
              label="Unique Calls"
            />

            <Button
              variant="contained"
              onClick={handleExport}
              startIcon={<Download />}
              disabled={isFetching || exportLoading || agentData.length === 0}
            >
              {exportLoading ? 'Exporting...' : 'Export Excel'}
            </Button>
          </Stack>
        </LocalizationProvider>

        {/* Display current selections */}
        <Paper elevation={1} sx={{ p: 2, backgroundColor: '#f5f5f5', border: '1px solid #d0d0d0' }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Period:</strong> {fromDate ? fromDate.format('DD MMM YYYY') : '-'} - {toDate ? toDate.format('DD MMM YYYY') : '-'} |
            <strong> Agents:</strong> {
              selectedUsers.includes('all')
                ? `All (${telecallerUsers.length})`
                : `Selected (${selectedUsers.length})`
            }
            {summary && (
              <>
                <br />
                <strong>Summary:</strong> {summary.totalAgents} agents, {summary.totalAllocated?.toLocaleString()} total allocated, {summary.overallAttemptPercentage}% attempt rate
              </>
            )}
          </Typography>
        </Paper>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load agent analytics: {error.message}
        </Alert>
      )}

      {/* Main Table */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <AgentAnalyticsTable
          title="Agent Performance Summary"
          data={agentData}
          loading={isFetching}
          error={error}
          fromDate={fromDate}
          toDate={toDate}
          sortable={true}
          showTotals={true}
        />
      </Box>
    </Paper>
  );
};